
import {
    PropType,
    computed,
    defineComponent, ref, watch,
} from 'vue';
import _isEqual from 'lodash.isequal';
import EntityDetail, { EntityDetailStyling } from './EntityDetail.view.vue';
import type { Component } from 'vue-router/types/router';
import { Location, Route, RouteConfig } from 'vue-router';
import { useDisabled, useEditing, useEntityMode } from './useEntityDetail';
import { EntityDetailMode } from './mode';
import { useRoute } from 'src/util/composables';
import type { EntityType } from 'types/entity';
import EntityTab, {
    EntityDetailRouteConfig,
    EntityTabsRoute,
    getEntityNavGuard,
    isEntityDetailRoute,
} from './entity-route-util';
import { ValidationResult } from 'types/routes';
import { accessor } from 'src/store';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router/composables';
import { useAsyncState, useEventListener } from '@vueuse/core';
import { Notify } from 'src/notifications';

/**
 * Checks if route `r2` would be redundant if navigated to from route `r1`
 */
function areRoutesRedundant (r1: Location, r2: Location): boolean {
    if (r1.name !== r2.name) {
        return false;
    }
    if (r1.query !== r2.query) {
        return false;
    }
    // params are preserved if they aren't specified, so we have to check for changed params in r2
    const r1Params = r1.params || {};
    const r2Params = r2.params || {};
    for (const paramName in r2Params) {
        if (r1Params[paramName] !== r2Params[paramName]) {
            return false;
        }
    }
    return true;
}

export default defineComponent({
    name: 'RouteEntityDetail',
    components: {
        EntityDetail,
    },
    props: {
        entityStyling: {
            default: null,
            required: false,
            type: Object as PropType<EntityDetailStyling>,
        },
        entityDetailRoute: {
            type: Object as PropType<EntityDetailRouteConfig<EntityType> | RouteConfig>, required: true,
        },
        tabsRoute: {
            type: Object as PropType<EntityTabsRoute | null>, required: false, default: null,
        },
    },
    setup (props) {
        const router = useRouter();
        const currentRoute = useRoute();


        // this hook is called when the components are reused.
        // we might need to refetch if the identifying props for the entity have changed (route params), or if entering fork/copy mode.
        onBeforeRouteUpdate((to, from, next) => {
            if (isEntityDetailRoute(props.entityDetailRoute)) {
                const routeParamsForFetch = props.entityDetailRoute.meta.routeParamsForFetch || (route => route.params);
                const paramsChanged = !_isEqual(routeParamsForFetch(to), routeParamsForFetch(from));
                const modeChanged = to.query.mode !== from.query.mode;

                /**
                 * just in case, we will also verify this RouterEntityDetail is being used for the `entityDetailRoute` it is given
                 */
                const routeIsRelevant = to.matched.find(route => route.name === props.entityDetailRoute.name);

                // if the params or mode has changed, redo the get
                if (routeIsRelevant && (paramsChanged || modeChanged)) {
                    return getEntityNavGuard(props.entityDetailRoute, to, from, next);
                }
            }
            next();
        });

        const tabs = computed<EntityTab[]>(() => {
            const result: EntityTab[] = [];
            const childrenRoutes: (RouteConfig & {
                hide?: boolean | ((route: Route) => boolean);
            })[] = (props.tabsRoute || props.entityDetailRoute).children || [];
            for (const childRoute of childrenRoutes) {
                const isHidden = typeof childRoute.hide === 'function' ? childRoute.hide(currentRoute.value) : childRoute.hide;
                if (childRoute.name && childRoute.meta?.tab?.label && !isHidden) {
                    if (!childRoute.meta.navigationGuard || childRoute.meta.navigationGuard(childRoute)) {
                        result.push({
                            attrs: {
                                to: { name: childRoute.name, query: currentRoute.value.query },
                                replace: true,
                            },
                            icon: childRoute.meta.tab.icon,
                            label: childRoute.meta.tab.label,
                        });
                    }
                }
            }
            return result;
        });

        const activeTabIdx = ref(0);
        watch(currentRoute, currentRouteVal => {
            for (const matchedRoute of currentRouteVal.matched) {
                const tabIdx = tabs.value.findIndex(tab => tab.attrs?.to?.name === matchedRoute.name && matchedRoute.name);
                if (tabIdx !== -1) {
                    if (activeTabIdx.value !== tabIdx) {
                        activeTabIdx.value = tabIdx;
                    }
                    return;
                }
            }
            activeTabIdx.value = -1;
        }, { immediate: true });
        // when tabs are clicked, navigation will automatically be performed. activeTabIdx will stay in sync too

        const {
            mode,
            editing: entityEditing,
            forking,
            disabled,
        } = useEntityMode();

        useEditing.useProvide(entityEditing);
        useDisabled.useProvide(disabled);

        /**
         * whether the user should be warned if there are unsaved changes before navigating away.
         * @default true
         */
        const warnUnsavedAllowed = computed(() => props.tabsRoute?.meta?.warnUnsaved ?? props.entityDetailRoute.meta?.warnUnsaved ?? true);

        /**
         * whether the lifecycle of the entity is managed by routes, or by child components.
         */
        const isRouteManagedEntity = computed(() => Boolean(isEntityDetailRoute(props.entityDetailRoute) && (props.entityDetailRoute.meta.saveEntity || props.entityDetailRoute.meta.createEntity)));

        const hasChanges = computed(() => {
            if (!entityEditing.value || !isRouteManagedEntity.value) {
                return false;
            }

            const hasChangesFn: (route: Route) => boolean = props.entityDetailRoute.meta?.hasChanges || (() => !_isEqual(accessor.entity?.entity, accessor.originalEntity?.entity));
            return hasChangesFn(currentRoute.value);
        });

        const returnRoute = computed(() => props.tabsRoute?.meta?.returnRoute || props.entityDetailRoute.meta?.returnRoute);

        const unsavedMsg = 'WARNING: unsaved edits will be lost, are you sure you want to continue?';
        /**
         * should be called before attempting to leave the entity with unsaved changes, honoring `warnUnsavedAllowed`.
         *
         * returns true if the user decided to navigate away from the page, otherwise false.
         * will default to true if warnUnsaved is falsey
         */
        function guardUnsaved () {
            if (warnUnsavedAllowed.value && hasChanges.value) {
                // eslint-disable-next-line no-alert
                return confirm(unsavedMsg);
            }
            return true;
        }

        // add/remove a listener to the window object to check if the user is navigating away from the page.
        // this covers actions like hard refresh, or closing the tab.
        useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
            if (entityEditing.value && !guardUnsaved()) {
                event.preventDefault();
                event.returnValue = unsavedMsg;
            }
        });

        function goBack () {
            if ([ 'function', 'object' ].includes(typeof returnRoute.value)) {
                const returnRouteTarget = typeof returnRoute.value === 'function' ? returnRoute.value(router.currentRoute) : returnRoute.value;
                router.push(returnRouteTarget);
            } else if (mode.value === 'create') {
                window.history.back();
            }
        }

        onBeforeRouteLeave((to, from, next) => {
            if (guardUnsaved()) {
                next();
            } else {
                // cancelled by user
                next(new Error('Navigation cancelled by user'));
            }
        });

        const { state: canFork } = useAsyncState(async () => {
                const routeCanFork = props.entityDetailRoute.meta?.canFork as EntityDetailRouteConfig<EntityType>['meta']['canFork'];
                if (typeof routeCanFork === 'function') {
                    const res = await routeCanFork(router);
                    return res;
                }
                return Boolean(routeCanFork);
            }, false);

        const editing = computed<boolean>({
                get: () => entityEditing.value,
                set: v => {
                    if (currentRoute.value.name) {
                        if (v) {
                            router.replace({
                                name: currentRoute.value.name,
                                params: currentRoute.value.params,
                                query: { ...currentRoute.value.query, mode: 'edit' },
                            });
                        } else { // handle "cancel" event
                            // eslint-disable-next-line no-lonely-if
                            if (isEntityDetailRoute(props.entityDetailRoute)) {
                                if ([
                                    EntityDetailMode.Edit,
                                ].includes(mode.value) && accessor.originalEntity) {
                                    // only check for unsaved changes if the entity is not new. all other cases will be handled by the onBeforeRouteLeave guard.
                                    // eslint-disable-next-line max-depth
                                    if (!guardUnsaved()) {
                                        // cancelled by user
                                        return;
                                    }

                                    accessor.initializeMaintenance({ type: accessor.originalEntity.type, entity: accessor.originalEntity.entity });
                                    router.replace({
                                        name: currentRoute.value.name,
                                        params: currentRoute.value.params,
                                        query: { ...currentRoute.value.query, mode: EntityDetailMode.View },
                                    });
                                } else {
                                    // create, fork, copy, etc
                                    goBack();
                                }
                            } else {
                                router.go(0);
                            }
                        }
                    }
                },
            });

        const modalComponent = ref<(() => Promise<Component>) | null>(null);
        const modalProps = ref<Record<string, unknown>>({});
        const saveModalOpen = ref(false);
        const saveEntity = async () => {
            if (props.entityDetailRoute.meta?.preSaveMaintenance) {
                props.entityDetailRoute.meta.preSaveMaintenance(accessor);
            }
            // stripping out everything not needed for route comparison
            const currentRouteMinimal: Location = {
                query: router.currentRoute.query,
                params: router.currentRoute.params,
                name: router.currentRoute.name as string,
            };

            saveModalOpen.value = false;
            if (!isEntityDetailRoute(props.entityDetailRoute)) {
                return;
            }

            let validationResult: ValidationResult = {
                status: 'success', // assume success until proven otherwise
            };

            // call the appropriate persistence method (create / save) and merge the result

            const entityData = accessor.entityAsType(props.entityDetailRoute.meta.entityType);
            if (!entityData) {
                return;
            }
            const originalEntityData = accessor.originalEntityAsType(props.entityDetailRoute.meta.entityType);

            /** needed by save functions for conditional validation */
            const featureFlags = accessor.featureFlags;
            
            // call the appropriate save function depending on the mode, updating the saveResult
            if (mode.value === 'create' || mode.value === 'copy') {
                if (props.entityDetailRoute.meta.createEntity) {
                    validationResult = await props.entityDetailRoute.meta.createEntity({
                        mode: mode.value,
                        entity: entityData,
                        featureFlags,
                    });
                } else if (props.entityDetailRoute.meta.saveEntity) { // default to saveEntity if createEntity is not defined
                    validationResult = await props.entityDetailRoute.meta.saveEntity({
                        oldEntity: null,
                        mode: mode.value,
                        entity: entityData,
                        featureFlags,
                    });
                }
            } else if (mode.value === 'fork') {
                if (props.entityDetailRoute.meta.forkEntity) {
                    validationResult = await props.entityDetailRoute.meta.forkEntity({
                        mode: mode.value,
                        entity: entityData,
                        oldEntity: originalEntityData,
                        featureFlags,
                    });
                } else if (props.entityDetailRoute.meta.saveEntity) { // default to saveEntity if forkEntity is not defined
                    validationResult = await props.entityDetailRoute.meta.saveEntity({
                        oldEntity: null,
                        mode: mode.value,
                        entity: entityData,
                        featureFlags,
                    });
                }
            } else if (props.entityDetailRoute.meta.saveEntity) {
                validationResult = await props.entityDetailRoute.meta.saveEntity({
                    entity: entityData,
                    oldEntity: originalEntityData,
                    mode: mode.value,
                    featureFlags,
                });
            }

            if (validationResult.status === 'success') { // if successful, reset the store and prepare nav
                const saveResultRoute: Location = {
                    ...currentRoute.value,
                    name: validationResult.newRoute?.name || currentRoute.value.name as string,
                    query: {
                        mode: 'view',
                    },
                };
                if (validationResult.newRoute?.params) {
                    saveResultRoute.params = validationResult.newRoute.params;
                }

                // avoid redundant nav
                if (!areRoutesRedundant(currentRouteMinimal, saveResultRoute)) {
                    console.log('Validation result success requested route change', saveResultRoute);
                    await router.replace({
                        ...saveResultRoute,
                        query: {
                            ...currentRoute.value.query,
                            ...(saveResultRoute.query ?? {}),
                        },
                    }).catch(err => console.error('router replace error', err));
                }
            } else if (validationResult.status === 'modal') {
                modalComponent.value = validationResult.modalComponent;
                modalProps.value = validationResult.modalProps || {};
                saveModalOpen.value = true;
            } else if (validationResult.status === 'error') {
                Notify.error(validationResult.errorMessage);
                if (validationResult.accessorActions) {
                    validationResult.accessorActions(accessor);
                }
                if (validationResult.routeName) {
                    // avoid redundant nav
                    if (currentRouteMinimal.name !== validationResult.routeName) {
                        console.log('Validation result error requested route change', validationResult.routeName);
                        await router.replace({
                            ...currentRoute.value,
                            name: validationResult.routeName,
                        }).catch(err => console.error('router replace error', err));
                    }
                }
            }
        };
        return {
            tabs,
            saveEntity,
            hasChanges,
            saveModalOpen,
            modalComponent,
            modalProps,
            warnUnsaved: warnUnsavedAllowed,
            activeTabIdx,
            isRouteManagedEntity,
            showBackBtn: computed(() => Boolean(returnRoute.value)),
            goBack,
            editing,
            forking,
            forkEntity () {
                router.push({
                    name: currentRoute.value.name!,
                    params: currentRoute.value.params,
                    query: { ...currentRoute.value.query, mode: 'fork' },
                });
            },
            canEdit: computed(() => {
                const routeCanEdit = props.entityDetailRoute.meta?.canEdit;
                if (typeof routeCanEdit === 'function') {
                    return routeCanEdit();
                }
                return Boolean(routeCanEdit);
            }),
            canFork,
            useEntityPadding: computed(() => {
                const useEntityPadding = props.entityDetailRoute.meta?.useEntityPadding === undefined ? true : props.entityDetailRoute.meta.useEntityPadding;
                return useEntityPadding;
            }),
        };
    },
});
